import { DateTime } from 'luxon';
import {
  useHeaderTitle,
  useHeaderTitleEnabled,
} from '@/features/experiences/contexts/PageExperienceContext';
import { isColorLegendVisible } from '@/features/experiences/lib/experience';
import type { PageExperience } from '@/features/experiences/types';
import { useDocumentContext } from '@/features/printables/contexts/DocumentContext';
import {
  FONT_HEADING,
  FONT_TEXT,
  FONT_CALENDAR_HEADING,
} from '@/features/printables/css/customFonts';
import { getStyleById } from '@/features/printables/theme';
import { useIsMobileBreakpoint } from '@/hooks/useIsBreakpoint';
import { Box, Flex, Text } from '@/ui';
import type { Month } from '../types';
import { Legend } from './Legend';

type Props = {
  experience: PageExperience;
  month: Month;
  pageIndex: number;
  pageIndexForMonth: number;
};

export const PageHeader = ({
  experience,
  month,
  pageIndex,
  pageIndexForMonth,
}: Props) => {
  const { isPrintEnv } = useDocumentContext();
  const isMobile = useIsMobileBreakpoint();
  const stackVertically = !isPrintEnv && isMobile;
  const theme = getStyleById(experience.styleId ?? undefined);
  // The eyebrow line shows the view's own header title, which defaults to the
  // owning planner's until the author sets one in the Views panel.
  const headerTitle = useHeaderTitle();
  const headerTitleEnabled = useHeaderTitleEnabled();

  const monthLabel = DateTime.fromObject({
    month: month.month,
    year: month.year,
  }).toFormat('LLLL yyyy');
  const pageNumber =
    pageIndexForMonth > 0 ? ` (Page ${pageIndexForMonth + 1})` : '';

  return (
    <Flex
      alignItems={stackVertically ? 'flex-start' : 'flex-end'}
      borderBottomStyle="solid"
      borderBottomWidth="0.125em"
      borderColor={theme.neutral5 || theme.accentColor}
      data-measure={`page-${pageIndex}-header`}
      flexDirection={stackVertically ? 'column' : 'row'}
      gap={stackVertically ? '0.5em' : undefined}
      justifyContent="space-between"
      mb="0.625em"
      pb={stackVertically ? '0.5em' : undefined}
    >
      <Flex
        alignItems="flex-start"
        flexDirection="column"
        gap="0.125em"
        minW={0}
        overflow="hidden"
      >
        {headerTitleEnabled && (
          <Box
            as="h1"
            color={theme.neutral3}
            fontFamily={FONT_TEXT}
            fontSize="0.8em"
            fontWeight={300}
            letterSpacing="0.1em"
            overflow="hidden"
            textOverflow="ellipsis"
            textTransform="uppercase"
            whiteSpace="nowrap"
          >
            {headerTitle}
          </Box>
        )}

        <Flex
          alignItems="baseline"
          flexDirection="row"
          gap="0.25em"
          justifyContent="flex-start"
          maxW="100%"
        >
          <Box
            as="h2"
            fontFamily={FONT_CALENDAR_HEADING}
            fontSize={stackVertically ? '2em' : '2.5em'}
            fontWeight="700"
            mb="0.25em"
            minW={0}
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {monthLabel}
          </Box>
          <Text
            as="span"
            flexShrink={0}
            fontFamily={FONT_HEADING}
            fontSize="1.25em"
            fontWeight="400"
            ml="1em"
          >
            {pageNumber}
          </Text>
        </Flex>
      </Flex>

      {isColorLegendVisible(experience) && <Legend experience={experience} />}
    </Flex>
  );
};
